// Copyright 2019 Sorama B.V.

import { BindingEngine, Disposable, ObserverLocator } from "aurelia-framework";
import { ObjectExtensions } from "extensions/ObjectExtensions";
import { IWorkflowModuleReference } from "../Interfaces/IWorkflowModule";
import { WorkflowPropertyBindingMode } from "../Interfaces/IWorkflowPropertyMetadata";
import { IWorkflowPropertyParser } from "../Interfaces/IWorkflowPropertyParser";
import { ScopedWorkflowProperty } from "./ModuleSetScopeObserver";
import { getWorkflowPropertyMetadata } from "./WorkflowPropertyAttribute";
/**
 * Observes a single property in a single workflow. Propagates changes to all observed properties in different modules.
 */
export class WorkflowPropertyObserver {
    protected _value: unknown;
    protected modules: IWorkflowModuleReference[] = [];
    protected observers: ((newValue: unknown) => void)[] = [];

    protected valueObserver: Disposable;
    protected valueCollectionObserver: Disposable;

    public get value(): unknown {
        return this._value;
    }

    public set value(val: unknown) {
        // Make sure that the property is in the correct format
        if (this.parser && typeof val == "string") {
            val = this.parser.fromWorkflowProperty(val);
        }

        if (!ObjectExtensions.deepEqual(this._value, val)) {
            this._value = val;
        }
    }

    public get workflowModuleTargetIds() {
        if (this._workflowModuleTargetIds == undefined) {
            return undefined;
        }

        // return a copy, preventing people playing with this
        return [...this._workflowModuleTargetIds];
    }

    constructor(
        private readonly bindingEngine: BindingEngine,
        public readonly propertyName: string,
        public objectType: string,
        protected readonly locator: ObserverLocator,
        private _workflowModuleTargetIds: number[] = undefined,
        private defaultValue: unknown = undefined,
        private readonly parser: IWorkflowPropertyParser<unknown> = undefined
    ) {
        this.valueObserver = this.bindingEngine.propertyObserver(this, "_value").subscribe((newValue, oldValue) => {
            this.valueChanged(newValue, oldValue);
        });
    }

    public addModule(
        module: IWorkflowModuleReference,
        bindingType: WorkflowPropertyBindingMode = WorkflowPropertyBindingMode.twoWay,
        nestedPropertyName?: string
    ) {
        if (this.modules.find((x) => x.moduleId == module.moduleId)) {
            throw new Error(
                "Module '" + module.moduleName + "' with property '" + this.propertyName + "' is already observed."
            );
        }

        if (
            this._workflowModuleTargetIds &&
            this._workflowModuleTargetIds.findIndex((x) => x === module.moduleId) === -1
        ) {
            throw new Error(
                "Module '" +
                    module.moduleName +
                    "' with property '" +
                    this.propertyName +
                    "' is not allowed in this scope."
            );
        }

        // Store the reference to the module
        this.modules.push(module);

        // Use the default if we do not have a value yet.
        if (this.value == undefined && this.defaultValue != undefined) {
            this.value = this.defaultValue;
        } else if (
            this.value == undefined &&
            module[this.propertyName] != undefined &&
            bindingType !== WorkflowPropertyBindingMode.oneWay
        ) {
            // There is no default value, so use the assigned value (if available)
            this.value = module[this.propertyName];
        }

        // Override the getter and setter of the object, so we can control it.
        Object.defineProperty(module, this.propertyName, {
            get: () => {
                return this.getValue(bindingType);
            },
            set: (val) => {
                this.setValue(val, bindingType);
            },
            configurable: true,
        });

        // Trigger a change handler for the new module if the value is defined
        if (this._value != undefined) {
            this.notifyModuleForValueChanged(module);
        }
    }

    public changeTargetModuleIds(newTargetIds: number[]): void {
        // Remove any reference to old modules, now outside scope
        for (const idx of this._workflowModuleTargetIds.filter((x) => newTargetIds.find((y) => x == y) == undefined)) {
            this.removeModuleById(idx);
        }

        this._workflowModuleTargetIds = newTargetIds;
    }

    public removeModule(module: IWorkflowModuleReference): void {
        const moduleIdx = this.modules.findIndex((x) => x == module);
        if (moduleIdx >= 0) {
            const removedModules = this.modules.splice(moduleIdx, 1);

            // Just assign the value
            Object.defineProperty(removedModules[0], this.propertyName, {
                value: ObjectExtensions.copy(this._value),
                writable: true,
                configurable: true,
            });
        }
    }

    public removeModuleById(moduleId: number): void {
        const moduleRef = this.modules.find((x) => x.moduleId == moduleId);

        if (moduleRef) this.removeModule(moduleRef);
    }

    public getModuleById(id: number): IWorkflowModuleReference {
        return this.modules.find((x) => x.moduleId == id);
    }

    public getModules(): IWorkflowModuleReference[] {
        return this.modules;
    }

    public dispose(): void {
        while (this.modules.length > 0) {
            this.removeModule(this.modules[0]);
        }

        if (this.valueObserver) {
            this.valueObserver.dispose();
            this.valueObserver = undefined;
        }

        if (this.valueCollectionObserver) {
            this.valueCollectionObserver.dispose();
            this.valueCollectionObserver = undefined;
        }
    }

    public subscribe(callback: (newValue: unknown) => void): Disposable {
        this.observers.push(callback);

        return <Disposable>{
            dispose: () => {
                if (this == undefined) {
                    // Original class is already disposed...
                    return;
                }

                const idx = this.observers.findIndex((x) => x == callback);
                if (idx != -1) this.observers.splice(idx, 1);
            },
        };
    }

    private valueChanged = (newValue, oldValue) => {
        if (newValue == oldValue) {
            return;
        }

        // Check if we need to renew the collection observer
        if (Array.isArray(this._value)) {
            if (this.valueCollectionObserver) this.valueCollectionObserver.dispose();

            this.valueCollectionObserver = this.bindingEngine
                .collectionObserver(this._value)
                .subscribe(() => this.valueCollectionChanged());
        }

        this.valueCollectionChanged();
    };

    private valueCollectionChanged = () => {
        for (const observer of this.observers) {
            observer(this._value);
        }

        for (const module of this.modules) {
            this.notifyModuleForValueChanged(module);
        }
    };

    private notifyModuleForValueChanged(mod: IWorkflowModuleReference): void {
        this.NotifyLocator(mod);

        if (typeof mod[this.propertyName + "Changed"] == "function") {
            mod[this.propertyName + "Changed"](this._value);
        }

        const propertySettings = getWorkflowPropertyMetadata(mod, this.propertyName);

        if (
            propertySettings &&
            propertySettings.changedHandler &&
            typeof mod[propertySettings.changedHandler] == "function"
        ) {
            mod[propertySettings.changedHandler](this._value);
        }
    }

    protected getValue(bindingType: WorkflowPropertyBindingMode): unknown {
        if (bindingType === WorkflowPropertyBindingMode.oneWay && this._value != undefined) {
            // Return a copy when it is a one way binding, so that we do not ruin the original value
            return ObjectExtensions.copy(this._value);
        }

        return this._value;
    }

    protected setValue(val: unknown, bindingType: WorkflowPropertyBindingMode) {
        if (bindingType !== WorkflowPropertyBindingMode.oneWay) {
            this.value = val;
        }
    }

    protected NotifyLocator(mod: IWorkflowModuleReference): void {
        if (this.locator) {
            const observer = this.locator.getObserver(mod, this.propertyName);

            if (observer) {
                observer.setValue(this._value);
            }
        }
    }
}

export class ScopedWorkflowPropertyObserver extends WorkflowPropertyObserver {
    constructor(
        bindingEngine: BindingEngine,
        propertyName: string,
        objectType: string,
        locator: ObserverLocator,
        private readonly masterModule: IWorkflowModuleReference,
        masterBindingMode: WorkflowPropertyBindingMode,
        private readonly targetModuleId: number,
        defaultValue: unknown,
        parser: IWorkflowPropertyParser<unknown> = undefined
    ) {
        super(
            bindingEngine,
            propertyName,
            objectType,
            locator,
            [masterModule.moduleId, targetModuleId],
            defaultValue,
            parser
        );

        // Store the reference to the module
        this.modules.push(masterModule);

        // Copy the value if we do not have a value yet
        const prop: ScopedWorkflowProperty<unknown> = masterModule[this.propertyName];
        if (
            this.value == undefined &&
            prop.has(targetModuleId) &&
            masterBindingMode !== WorkflowPropertyBindingMode.oneWay
        ) {
            this.value = prop.get(targetModuleId);
        }

        // Override the getter and setter of the object, so we can control it.
        Object.defineProperty(masterModule[this.propertyName], targetModuleId, {
            get: () => {
                return this.getValue(masterBindingMode);
            },
            set: (val) => {
                this.setValue(val, masterBindingMode);
            },
            configurable: true,
        });
    }

    public addModule(
        module: IWorkflowModuleReference,
        bindingType: WorkflowPropertyBindingMode = WorkflowPropertyBindingMode.twoWay,
        nestedPropertyName?: string
    ) {
        if (module.moduleId != this.targetModuleId && this.masterModule != module) {
            throw new Error("Added module is not in the scope of this observer");
        }

        super.addModule(module, bindingType, nestedPropertyName);
        this.NotifyLocator(module); // Make sure that bindings are updated
    }

    public removeModule(module: IWorkflowModuleReference): void {
        const moduleIdx = this.modules.findIndex((x) => x == module);

        if (moduleIdx >= 0) {
            const removedModules = this.modules.splice(moduleIdx, 1);
            const prop: ScopedWorkflowProperty<unknown> = this.masterModule[this.propertyName];

            // Just assign the value
            if (module != this.masterModule) {
                // Make sure that the master has no reference left to this module
                prop.remove(module.moduleId);

                Object.defineProperty(removedModules[0], this.propertyName, {
                    value: ObjectExtensions.copy(this._value),
                    writable: true,
                    configurable: true,
                });
            } else {
                Object.defineProperty(this.masterModule[this.propertyName], this.targetModuleId, {
                    value: undefined,
                    writable: true,
                    configurable: true,
                });

                // Make sure that the prop itself is also notified about the change
                prop.remove(this.targetModuleId);
            }
        }
    }

    protected NotifyLocator(module: IWorkflowModuleReference): void {
        if (this.locator) {
            // We do not want to override the mastermodule value.
            if (this.masterModule.moduleId != module.moduleId) {
                const observer = this.locator.getObserver(module, this.propertyName);
                if (observer) {
                    observer.setValue(this._value);
                }
            }
        }
    }
}
